"use client"

import { cookieStorage } from "@/lib/cookie-storage"
import { DomainEntity } from "@/types/domain"
import { create } from "zustand"
import { persist } from "zustand/middleware"

export const DEFAULT_DOMAIN: DomainEntity = {
  id: 0,
  attributes: {
    name: "All websites",
    domain: "",
    createdAt: "",
    updatedAt: "",
    publishedAt: "",
  },
}

interface DomainState {
  selectedDomain?: DomainEntity
  setSelectedDomain: (domain?: DomainEntity) => void
  domains: DomainEntity[]
  setDomainsFromWebsites: (websites?: unknown[]) => void
  initialize: () => Promise<void>
  /** Clear selection + domain list. Called on logout. */
  clearDomain: () => void
  hasHydrated: boolean
  setHasHydrated: (value: boolean) => void
  /** True when a concrete single-site scope is active (not "All websites"). */
  hasConcreteSite: () => boolean
  /** True when the user is allowed more than one site and must pick. */
  needsSiteSelection: () => boolean
}

type RawWebsite = {
  id?: number | string
  name?: string
  domain?: string
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
  attributes?: {
    name?: string
    domain?: string
    createdAt?: string
    updatedAt?: string
    publishedAt?: string
  }
}

const mapWebsitesToDomains = (websites: unknown[] = []): DomainEntity[] => {
  const mappedDomains = websites
    .map((website): DomainEntity | null => {
      const websiteData = website as RawWebsite
      const id = Number(websiteData?.id)
      const source = websiteData?.attributes || websiteData

      if (!id) {
        return null
      }

      const domain = source?.domain || ""
      const name = source?.name || domain

      return {
        id,
        attributes: {
          name,
          domain,
          createdAt: source?.createdAt || "",
          updatedAt: source?.updatedAt || "",
          publishedAt: source?.publishedAt || "",
        },
      }
    })
    .filter((domain): domain is DomainEntity => Boolean(domain))

  if (mappedDomains.length <= 1) {
    return mappedDomains
  }

  return [DEFAULT_DOMAIN, ...mappedDomains]
}

export const useDomainStore = create<DomainState>()(
  persist(
    (set, get) => ({
      selectedDomain: DEFAULT_DOMAIN,
      domains: [],
      hasHydrated: false,

      setSelectedDomain: selectedDomain => set({ selectedDomain }),
      setHasHydrated: hasHydrated => set({ hasHydrated }),
      setDomainsFromWebsites: websites => {
        const nextDomains = mapWebsitesToDomains(websites || [])
        const allowedDomains = nextDomains.filter(domain => domain.id !== DEFAULT_DOMAIN.id)
        const currentSelectedDomain = get().selectedDomain
        const hasSelectedDomainInList = nextDomains.some(domain => domain.id === currentSelectedDomain?.id)

        // If user has only one allowed domain, force-select it by default.
        const nextSelectedDomain =
          allowedDomains.length === 1
            ? allowedDomains[0]
            : hasSelectedDomainInList
              ? currentSelectedDomain
              : DEFAULT_DOMAIN

        set({
          domains: nextDomains,
          selectedDomain: nextSelectedDomain,
        })
      },
      initialize: async () => {
        try {
          const savedUser = localStorage.getItem("user")
          const parsedUser = savedUser ? JSON.parse(savedUser) : null
          // Website scope now lives directly on the user (not the role).
          const websites = Array.isArray(parsedUser?.websites) ? parsedUser.websites : []

          get().setDomainsFromWebsites(websites)
        } catch (error) {
          console.error("Error initializing domains:", error)
        }
      },
      clearDomain: () => set({ selectedDomain: DEFAULT_DOMAIN, domains: [] }),
      hasConcreteSite: () => {
        const s = get().selectedDomain
        return !!s && s.id !== DEFAULT_DOMAIN.id && !!s.attributes?.domain
      },
      needsSiteSelection: () => {
        const { domains, selectedDomain } = get()
        const real = domains.filter(d => d.id !== DEFAULT_DOMAIN.id)
        if (real.length <= 1) return false
        if (!selectedDomain || selectedDomain.id === DEFAULT_DOMAIN.id) return true
        return !real.some(d => d.id === selectedDomain.id)
      },
    }),
    {
      name: "domain-storage",
      storage: cookieStorage,
      partialize: (state: DomainState) => ({
        selectedDomain: state.selectedDomain || DEFAULT_DOMAIN,
      }),
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true)
      },
    },
  ),
)

// Helper function để khởi tạo store và fetch domains sẵn
// Gọi trong AdminShell sau khi user hydrate, KHÔNG auto-init ở module level
// (tránh race condition với SSR + Next App Router streaming).
export const initializeDomainStore = () => {
  const { initialize } = useDomainStore.getState()
  return initialize()
}
